import React, { FC, useState } from 'react';
import styled from 'styled-components';

import { FlexColumnCentredNoMargin } from '../../common-styles/common-styles';
import { DisplayMatchResult } from '../../../api/hooks/use-fetch-team-totals-by-day';
import { ResultMarginDisplay } from './result-margin';
import { ResultScoreCard } from './result-scorecard';

const getRightArrowColour = (result: string): string => {
  switch (result) {
    case 'beat':
      return 'red';
    case 'lost to':
      return '#eae6e6';
    default:
      return '#d884f0';
  }
};
const getLeftArrowColour = (result: string): string => {
  switch (result) {
    case 'beat':
      return '#eae6e6';
    case 'lost to':
      return 'blue';
    default:
      return '#d884f0';
  }
};

const ResultWrapper = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  background: #eae6e6;
  border: 1px solid lightgray;
  border-radius: 5px;
  width: 100%;
`;

const MatchDisplay = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const ArrowRight = styled.div<{ bgColor?: string | null }>`
  width: 0;
  height: 0;
  border-top: 2rem solid transparent;
  border-bottom: 2rem solid transparent;

  border-left: ${(props) => `1.5rem solid ${props.bgColor}` || 'white'};
`;
const ArrowLeft = styled.div<{ bgColor?: string | null }>`
  width: 0;
  height: 0;
  border-top: 2rem solid transparent;
  border-bottom: 2rem solid transparent;
  border-right: ${(props) => `1.5rem solid ${props.bgColor}` || 'white'};
`;
const PlayerDisplay = styled.div<{ bgColor?: string | null }>`
  width: 5rem;
  background: ${(props) => props.bgColor || '#eae6e6'};
  color: ${(props) => (props.bgColor === '#eae6e6' ? 'black' : 'white')};
  text-align: center;
  vertical-align: middle;
  padding: 0.25rem;
  height: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ResultMarginWrapper = styled.div<{ bgColor?: string | null }>`
  margin: 0 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;

const Button = styled.button`
  border: none;
  background: transparent;
  height: 1rem;
  padding-top: 0.25rem;
`;
const getScoreColour = (result: string) => {
  switch (result) {
    case 'beat': {
      return 'red';
    }
    case 'lost to': {
      return 'blue';
    }
    default: {
      return 'grey';
    }
  }
};

export const Arrow = styled.i`
  display: inline-block;
  width: 10px;
  height: 10px;
  vertical-align: middle;
  margin: 0 0.25rem;
`;

export const ArrowUp = styled(Arrow)<{ color: string }>`
  border: ${(props) => `solid ${props.color}`};
  border-width: 0 4px 4px 0;
  padding: 4px;
  transform: rotate(-135deg);
  margin-top: 0.5rem;
`;

export const ArrowDown = styled(Arrow)<{ color: string }>`
  border: ${(props) => `solid ${props.color}`};
  border-width: 0 4px 4px 0;
  padding: 4px;
  transform: rotate(45deg);
  margin-top: 0.25rem;
`;

type Props = {
  match: DisplayMatchResult;
  tourIndex: number;
  startingHole: number;
  dayIndex: number;
  matchIndex: number;
  format: 'Pairs' | 'Singles';
};

export const MatchResult: FC<Props> = ({ match, tourIndex, startingHole, dayIndex, matchIndex, format }): JSX.Element => {
  const [showScoreCard, setShowScoreCard] = useState<boolean>(false);

  return (
    <FlexColumnCentredNoMargin>
      <ResultWrapper>
        <MatchDisplay>
          <PlayerDisplay bgColor={getRightArrowColour(match.result)}>
            {match.team1Players.map((player, index: number) => (
              <div key={`player-${index}`}>
                {index === 1 && <div>&</div>}
                <div>{player}</div>
              </div>
            ))}
          </PlayerDisplay>
          <ArrowRight bgColor={getRightArrowColour(match.result)} />
        </MatchDisplay>
        <ResultMarginWrapper>
          <ResultMarginDisplay margin={match?.margin ?? ''} color={getScoreColour(match.result)} />
          {match.hasScoreCardData && (
            <Button
              data-testid={`show-scorecard-${matchIndex}`}
              onClick={() => {
                setShowScoreCard(!showScoreCard);
              }}
            >
              {showScoreCard ? <ArrowUp color={getScoreColour(match.result)} /> : <ArrowDown color={getScoreColour(match.result)} />}
            </Button>
          )}
        </ResultMarginWrapper>
        <MatchDisplay>
          <ArrowLeft bgColor={getLeftArrowColour(match.result)} />
          <PlayerDisplay bgColor={getLeftArrowColour(match.result)}>
            {match.team2Players.map((player, index) => (
              <div key={`team-${index}`}>
                {index === 1 && <div>&</div>}
                <div>{player}</div>
              </div>
            ))}
          </PlayerDisplay>
        </MatchDisplay>
      </ResultWrapper>
      {showScoreCard && <ResultScoreCard tournamentIndex={tourIndex} startingHole={startingHole} dayIndex={dayIndex} matchIndex={matchIndex} format={format} />}
    </FlexColumnCentredNoMargin>
  );
};
